import BaseApiClient from "../api/baseApiClient.service";
import {
	OrgaAdminKlausurService,
	type Klausur,
} from "../orgaAdmin/orgaAdminKlausur.service";

export interface CreateKlausurPayload {
	title: string;
}

export interface CreateKlausurResponse {
	title: string;
	index: number;
}

export interface CreateAufgabenstellungPayload {
	title?: string;
}

export interface CreateAufgabenstellungResponse {
	id: string;
	title?: string;
	index: number;
}

export interface UpdateAufgabenstellungPayload {
	title: string;
}

export interface CreateAndUpdateSachverhaltPayload {
	title?: string;
}

export interface CreateSachverhaltResponse {
	id: string;
	title?: string;
	index: number;
}

export interface UpdateIndexItem {
	id: string;
	index: number;
}

export interface CreateAndUpdateMusterloesungPayload {
	title?: string;
}

export interface CreateMusterloesungResponse {
	id: string;
	title?: string;
	index: number;
}

export interface CreateAndUpdateKorrekturbogenItemPayload {
	text: string;
	maxPoints: number;
}

export interface CreateKorrekturbogenItemResponse {
	id: string;
	title?: string;
	index: number;
}

export type BulkUpdateIndexesPayload = UpdateIndexItem[];

export type KlausurComponentType =
	| "aufgabenstellung"
	| "sachverhalt"
	| "musterloesung"
	| "korrekturbogenitem";

/**
 * Service for the klausur
 * This service is used to get the klausur data
 * This will only work for organization admins
 */
export class KlausurService {
	private static instance: KlausurService;
	private baseApi: BaseApiClient;

	private baseApiSubPath = "/klausur";

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): KlausurService {
		if (!KlausurService.instance) {
			KlausurService.instance = new KlausurService();
			KlausurService.instance.baseApi = BaseApiClient.getInstance();
		}
		return KlausurService.instance;
	}

	/**
	 * Create a new klausur
	 * @param organizationId - The id of the organization
	 * @param klausur - The klausur data
	 * @returns The klausur data
	 */
	createKlausur(
		organizationId: string,
		klausur: CreateKlausurPayload,
	): Promise<CreateKlausurResponse> {
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/new`,
			klausur,
		);
	}

	/**
	 * Get all klausuren for an organization
	 * @param organizationId - The id of the organization
	 * @returns The klausuren
	 */
	public async getKlausuren(organizationId: string): Promise<Klausur[]> {
		return OrgaAdminKlausurService.getInstance().getKlausurs(organizationId);
	}

	/**
	 * Get a detailed klausur by id
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @returns The klausur
	 */
	public async getKlausurById(
		organizationId: string,
		klausurId: string,
	): Promise<Klausur> {
		if (!organizationId || !klausurId) {
			throw new Error("Organization ID and klausur ID are required");
		}
		return this.baseApi.get<Klausur>(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}`,
		);
	}

	/**
	 * Create a new aufgabenstellung for a klausur
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param payload - Optional payload (e.g. title)
	 * @returns The created aufgabenstellung metadata
	 */
	public createAufgabenstellung(
		organizationId: string,
		klausurId: string,
		payload?: CreateAufgabenstellungPayload,
	): Promise<CreateAufgabenstellungResponse> {
		if (!organizationId || !klausurId) {
			throw new Error("Organization ID and klausur ID are required");
		}
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/new-aufgabenstellung`,
			payload ?? {},
		);
	}

	/**
	 * Update an aufgabenstellung for a klausur
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param aufgabenstellungId - The id of the aufgabenstellung
	 * @param payload - Payload (e.g. title)
	 * @returns The updated aufgabenstellung metadata
	 */
	public updateAufgabenstellung(
		organizationId: string,
		klausurId: string,
		aufgabenstellungId: string,
		payload: UpdateAufgabenstellungPayload,
	): Promise<CreateAufgabenstellungResponse> {
		if (!organizationId || !klausurId || !aufgabenstellungId) {
			throw new Error(
				"Organization ID, klausur ID and aufgabenstellung ID are required",
			);
		}
		return this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/aufgabenstellung/${aufgabenstellungId}`,
			payload,
		);
	}

	/**
	 * Create a new sachverhalt under an aufgabenstellung
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param aufgabenstellungId - The id of the aufgabenstellung
	 * @param payload - Optional payload (e.g. title)
	 * @returns The created sachverhalt metadata
	 */
	public createSachverhalt(
		organizationId: string,
		klausurId: string,
		aufgabenstellungId: string,
		payload?: CreateAndUpdateSachverhaltPayload,
	): Promise<CreateSachverhaltResponse> {
		if (!organizationId || !klausurId || !aufgabenstellungId) {
			throw new Error(
				"Organization ID, klausur ID and aufgabenstellung ID are required",
			);
		}
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/aufgabenstellung/${aufgabenstellungId}/new-sachverhalt`,
			payload ?? {},
		);
	}

	/**
	 * Update a sachverhalt for an aufgabenstellung
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param sachverhaltId - The id of the sachverhalt
	 * @param payload - Optional payload (e.g. title)
	 * @returns The updated sachverhalt metadata
	 */
	public updateSachverhalt(
		organizationId: string,
		klausurId: string,
		sachverhaltId: string,
		payload: CreateAndUpdateSachverhaltPayload,
	): Promise<CreateSachverhaltResponse> {
		return this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/sachverhalt/${sachverhaltId}`,
			payload,
		);
	}

	/**
	 * Bulk update indexes for all sachverhalte in a klausur
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param payload - Array of id/index pairs
	 */
	public bulkUpdateSachverhalteIndexes(
		organizationId: string,
		klausurId: string,
		payload: BulkUpdateIndexesPayload,
	): Promise<void> {
		if (!organizationId || !klausurId) {
			throw new Error("Organization ID and klausur ID are required");
		}
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/sachverhalte/indexes`,
			payload,
		);
	}

	/**
	 * Bulk update indexes for all aufgabenstellungen in a klausur
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param payload - Array of id/index pairs
	 */
	public bulkUpdateAufgabenstellungenIndexes(
		organizationId: string,
		klausurId: string,
		payload: BulkUpdateIndexesPayload,
	): Promise<void> {
		if (!organizationId || !klausurId) {
			throw new Error("Organization ID and klausur ID are required");
		}
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/aufgabenstellungen/indexes`,
			payload,
		);
	}

	/**
	 * Bulk update indexes for all korrekturbogen items in a klausur
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param payload - Array of id/index pairs
	 */
	public bulkUpdateKorrekturbogenItemsIndexes(
		organizationId: string,
		klausurId: string,
		payload: BulkUpdateIndexesPayload,
	): Promise<void> {
		if (!organizationId || !klausurId) {
			throw new Error("Organization ID and klausur ID are required");
		}
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/korrekturbogenitems/indexes`,
			payload,
		);
	}

	/**
	 * Bulk update indexes for all musterloesungen in a klausur
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param payload - Array of id/index pairs
	 */
	public bulkUpdateMusterloesungenIndexes(
		organizationId: string,
		klausurId: string,
		payload: BulkUpdateIndexesPayload,
	): Promise<void> {
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/musterloesungen/indexes`,
			payload,
		);
	}

	/**
	 * Create a new musterloesung for a klausur in a sachverhalt
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param payload - Optional payload (e.g. title)
	 * @returns The created musterloesung metadata
	 */
	public async createMusterloesung(
		organizationId: string,
		klausurId: string,
		sachverhaltId: string,
		payload: CreateAndUpdateMusterloesungPayload,
	): Promise<CreateMusterloesungResponse> {
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/sachverhalt/${sachverhaltId}/new-musterloesung`,
			payload,
		);
	}

	/**
	 * Update a musterloesung for a klausur in a sachverhalt
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param musterloesungId - The id of the musterloesung
	 * @param payload - Optional payload (e.g. title)
	 * @returns The updated musterloesung metadata
	 */
	public async updateMusterloesung(
		organizationId: string,
		klausurId: string,
		musterloesungId: string,
		payload: CreateAndUpdateMusterloesungPayload,
	): Promise<CreateMusterloesungResponse> {
		KlausurService.dispatchSavingStarted("updateMusterloesung");
		try {
			return await this.baseApi.put(
				`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/musterloesung/${musterloesungId}`,
				payload,
			);
		} finally {
			KlausurService.dispatchSavingFinished("updateMusterloesung");
		}
	}

	/**
	 * Create new korrekturbogen item for a musterloesung
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param musterloesungId - The id of the musterloesung
	 * @param payload - Optional payload (e.g. title)
	 * @returns The created korrekturbogen item metadata
	 */
	public async createKorrekturbogenItem(
		organizationId: string,
		klausurId: string,
		musterloesungId: string,
		payload: CreateAndUpdateKorrekturbogenItemPayload,
	): Promise<CreateKorrekturbogenItemResponse> {
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/musterloesung/${musterloesungId}/new-korrekturbogenitem`,
			payload,
		);
	}

	/**
	 * Update a korrekturbogen item for a musterloesung
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param korrekturbogenItemId - The id of the korrekturbogen item
	 * @param payload - Optional payload (e.g. title)
	 * @returns The updated korrekturbogen item metadata
	 */
	public async updateKorrekturbogenItem(
		organizationId: string,
		klausurId: string,
		korrekturbogenItemId: string,
		payload: CreateAndUpdateKorrekturbogenItemPayload,
	): Promise<CreateKorrekturbogenItemResponse> {
		KlausurService.dispatchSavingStarted("updateKorrekturbogenItem");
		try {
			return await this.baseApi.put(
				`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/korrekturbogenitem/${korrekturbogenItemId}`,
				payload,
			);
		} finally {
			KlausurService.dispatchSavingFinished("updateKorrekturbogenItem");
		}
	}

	/**
	 * Delete a component within a klausur
	 * Matches backend route:
	 * "/:organizationId/klausur/:klausurId/:componentType/:component/:componentId"
	 * @param organizationId - The id of the organization
	 * @param klausurId - The id of the klausur
	 * @param componentType - The type of the component to delete
	 * @param component - The parent component id (if applicable)
	 * @param componentId - The id of the component to delete
	 */
	public async deleteComponent(
		organizationId: string,
		klausurId: string,
		componentType: KlausurComponentType,
		component: string,
		componentId: string,
	): Promise<void> {
		if (!organizationId || !klausurId || !componentType || !componentId) {
			throw new Error(
				"Organization ID, klausur ID, componentType and componentId are required",
			);
		}
		await this.baseApi.delete(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/${componentType}/${component}/${componentId}`,
		);
	}

	/**
	 * Dispatch a custom event to indicate that saving has started
	 * @param source - The source of the saving
	 */
	private static dispatchSavingStarted(source: string) {
		document.dispatchEvent(
			new CustomEvent("saving-started", {
				detail: { source },
			}),
		);
	}

	/**
	 * Dispatch a custom event to indicate that saving has finished
	 * @param source - The source of the saving
	 */
	private static dispatchSavingFinished(source: string) {
		document.dispatchEvent(
			new CustomEvent("saving-finished", {
				detail: { source },
			}),
		);
	}
}
